<template>
  <div v-if="editor">
    <v-toolbar dense>
      <v-Btn
        @click="editor.chain().focus().toggleBold().run()"
        :class="{ 'is-active': editor.isActive('bold') }"
        :outlined="editor.isActive('bold')"
        small
        icon
      >
        <v-icon>format_bold</v-icon>
      </v-Btn>
      <v-Btn
        @click="editor.chain().focus().toggleItalic().run()"
        :class="{ 'is-active': editor.isActive('italic') }"
        :outlined="editor.isActive('italic')"
        small
        icon
      >
        <v-icon>format_italic</v-icon>
      </v-Btn>
      <v-Btn
        @click="editor.chain().focus().toggleStrike().run()"
        :class="{ 'is-active': editor.isActive('strike') }"
        :outlined="editor.isActive('strike')"
        small
        icon
      >
        <v-icon>strikethrough_s</v-icon>
      </v-Btn>
      <v-Btn
        @click="editor.chain().focus().toggleCode().run()"
        :class="{ 'is-active': editor.isActive('code') }"
        :outlined="editor.isActive('code')"
        small
        icon
      >
        <v-icon>code</v-icon> </v-Btn
      ><v-divider vertical></v-divider>&nbsp;
      <v-btn
        @click="editor.chain().focus().setTextAlign('left').run()"
        :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
        :outlined="editor.isActive({ textAlign: 'left' })"
        small
        icon
      >
        <v-icon>format_align_left</v-icon>
      </v-btn>
      <v-btn
        @click="editor.chain().focus().setTextAlign('center').run()"
        :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }"
        :outlined="editor.isActive({ textAlign: 'center' })"
        small
        icon
      >
        <v-icon>format_align_center</v-icon>
      </v-btn>
      <v-btn
        @click="editor.chain().focus().setTextAlign('right').run()"
        :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }"
        :outlined="editor.isActive({ textAlign: 'right' })"
        small
        icon
      >
        <v-icon>format_align_right</v-icon>
      </v-btn>
      <v-divider vertical></v-divider>&nbsp;
      <v-Btn
        @click="editor.chain().focus().setParagraph().run()"
        :class="{ 'is-active': editor.isActive('paragraph') }"
        :outlined="editor.isActive('paragraph')"
        small
        icon
      >
        <v-icon>mdi-format-paragraph</v-icon>
      </v-Btn>
      <v-Btn
        @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
        :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
        :outlined="editor.isActive('heading', { level: 1 })"
        small
        icon
      >
        H1
      </v-Btn>
      <v-Btn
        @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
        :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
        :outlined="editor.isActive('heading', { level: 2 })"
        small
        icon
      >
        H2
      </v-Btn>
      <v-Btn
        @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
        :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
        :outlined="editor.isActive('heading', { level: 3 })"
        small
        icon
      >
        H3
      </v-Btn>
      <v-divider vertical></v-divider>&nbsp;
      <v-Btn
        @click="editor.chain().focus().toggleBulletList().run()"
        :class="{ 'is-active': editor.isActive('bulletList') }"
        :outlined="editor.isActive('bulletList')"
        small
        icon
      >
        <v-icon>format_list_bulleted</v-icon>
      </v-Btn>
      <v-Btn
        @click="editor.chain().focus().toggleOrderedList().run()"
        :class="{ 'is-active': editor.isActive('orderedList') }"
        :outlined="editor.isActive('orderedList')"
        small
        icon
      >
        <v-icon>format_list_numbered</v-icon>
      </v-Btn>
      <v-divider vertical></v-divider>&nbsp;
      <v-Btn
        @click="editor.chain().focus().toggleCodeBlock().run()"
        :class="{ 'is-active': editor.isActive('codeBlock') }"
        :outlined="editor.isActive('codeBlock')"
        small
        icon
      >
        <v-icon>integration_instructions</v-icon>
      </v-Btn>
      <v-Btn
        @click="editor.chain().focus().toggleBlockquote().run()"
        :class="{ 'is-active': editor.isActive('blockquote') }"
        :outlined="editor.isActive('blockquote')"
        small
        icon
      >
        <v-icon>format_quote</v-icon>
      </v-Btn>
      &nbsp;
      <v-Btn
        @click="editor.chain().focus().setHorizontalRule().run()"
        small
        icon
      >
        <v-icon>horizontal_rule</v-icon>
      </v-Btn>
      <v-Btn @click="editor.chain().focus().setHardBreak().run()" small icon>
        <v-icon>insert_page_break</v-icon>
      </v-Btn>
      <v-divider vertical></v-divider>&nbsp;
      <v-Btn @click="editor.chain().focus().undo().run()" small icon>
        <v-icon>undo</v-icon>
      </v-Btn>
      <v-Btn @click="editor.chain().focus().redo().run()" small icon>
        <v-icon>redo</v-icon>
      </v-Btn>
      <v-divider vertical></v-divider>&nbsp;
      <v-Btn @click="editor.chain().focus().unsetAllMarks().run()" small icon>
        <v-icon>format_clear</v-icon>
      </v-Btn>
      <v-Btn @click="editor.chain().focus().clearNodes().run()" small icon>
        <v-icon>layers_clear</v-icon>
      </v-Btn>
      <v-divider vertical></v-divider>&nbsp;
      <v-Btn
        @click="setLink"
        :class="{ 'is-active': editor.isActive('link') }"
        :outlined="editor.isActive('link')"
        small
        icon
      >
        <v-icon>add_link</v-icon>
      </v-Btn>
      <v-Btn
        @click="editor.chain().focus().unsetLink().run()"
        :disabled="!editor.isActive('link')"
        small
        icon
      >
        <v-icon>link_off</v-icon>
      </v-Btn>
      <v-overflow-btn
        v-model="editor.getAttributes('textStyle').color"
        :items="colorItems"
        label="couleur"
        hide-details
        dense
        item-text="nom"
        item-value="color"
        return-object
        single-line
        @change="colorChangedValue"
        ><template slot="selection" slot-scope="data">
          <!-- HTML that describe how select should render selected items -->
          <span :style="'color:' + data.item.color"> {{ data.item.nom }}</span>
        </template>
        <template slot="item" slot-scope="data">
          <!-- HTML that describe how select should render items when the select is open -->
          <span :style="'color:' + data.item.color"> {{ data.item.nom }}</span>
        </template></v-overflow-btn
      >
      <v-overflow-btn
        v-model="editor.getAttributes('highlight').color"
        :items="colorItems"
        label="couleur du fond"
        hide-details
        dense
        item-text="nom"
        item-value="color"
        return-object
        single-line
        @change="highlightChangedValue"
        ><template slot="selection" slot-scope="data">
          <!-- HTML that describe how select should render selected items -->
          <span :style="'background-color:' + data.item.color">
            {{ data.item.nom }}</span
          >
        </template>
        <template slot="item" slot-scope="data">
          <!-- HTML that describe how select should render items when the select is open -->
          <span :style="'background-color:' + data.item.color">
            {{ data.item.nom }}</span
          >
        </template></v-overflow-btn
      >
    </v-toolbar>
    <editor-content
      :editor="editor"
      style="
        border: solid;
        border-width: thin;
        overflow-y: scroll;
        max-height: 400px;
      "
    />
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";

export default {
  components: {
    EditorContent,
  },

  props: {
    value: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      editor: null,
      colorSelect: { nom: "Aucune", color: "" },
      highlightSelect: { nom: "Aucune", color: "" },
      colorItems: [
        { nom: "Rouge", color: "red" },
        { nom: "Vert", color: "green" },
        { nom: "Bleu", color: "blue" },
        { nom: "Jaune", color: "yellow" },
        { nom: "Aucune", color: "" },
      ],
    };
  },

  watch: {
    value(value) {
     
      const isSame = this.editor.getHTML() === value;

     
     

      if (isSame) {
        return;
      }

      this.editor.commands.setContent(value, false);
    },
  },

  mounted() {
    this.editor = new Editor({
      content: this.value,
     
      extensions: [
        StarterKit,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        Link.configure({
          openOnClick: false,
        }),
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
      ],
      onUpdate: () => {
       
        this.$emit("input", this.editor.getHTML());

       
       
      },
    });
  },

  beforeDestroy() {
    this.editor.destroy();
  },

  methods: {
    setLink() {
      const previousUrl = this.editor.getAttributes("link").href;
      const url = window.prompt("URL", previousUrl);

     
      if (url === null) {
        return;
      }

     
      if (url === "") {
        this.editor.chain().focus().extendMarkRange("link").unsetLink().run();

        return;
      }

     
      this.editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url, target: "_blank" })
        .run();
    },
    colorChangedValue: function (item) {
      if (item.color == "") {
        this.editor.chain().focus().unsetColor().run();
      } else this.editor.chain().focus().setColor(item.color).run();
    },
    highlightChangedValue: function (item) {
      if (item.color == "") {
        this.editor.chain().focus().unsetHighlight().run();
      } else
        this.editor
          .chain()
          .focus()
          .toggleHighlight({ color: item.color })
          .run();
    },
  },
};
</script>


<style lang="scss">
/* Basic editor styles */
.ProseMirror {
  > * + * {
    margin-top: 0.75em;
  }

  ul,
  ol {
    padding: 0 1rem;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.1;
  }

  code {
    background-color: rgba(#616161, 0.1);
    color: #616161;
  }

  pre {
    background: #0d0d0d;
    color: #fff;
    font-family: "JetBrainsMono", monospace;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;

    code {
      color: inherit;
      padding: 0;
      background: none;
      font-size: 0.8rem;
    }
  }

  img {
    max-width: 100%;
    height: auto;
  }

  blockquote {
    padding-left: 1rem;
    border-left: 2px solid rgba(#0d0d0d, 0.1);
  }

  hr {
    border: none;
    border-top: 2px solid rgba(#0d0d0d, 0.1);
    margin: 2rem 0;
  }
}
</style>